import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transferRequestApi } from '@/api/transferRequestApi';
import { useUIStore } from '@/store/uiStore';
import { QK } from './queryKeys';
import type { CreateTransferRequestPayload, TransferRequestFilter } from '@/types';
import { getApiErrorMessage } from '@/utils';

export function useTransferRequests(filters: TransferRequestFilter) {
  return useQuery({
    queryKey: QK.TRANSFER_REQUESTS_LIST(filters),
    queryFn: () => transferRequestApi.getList(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useGuestTransferRequests(filters: TransferRequestFilter) {
  return useQuery({
    queryKey: ['guest', 'transfer-requests', filters],
    queryFn: () => transferRequestApi.getGuestList(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 2,
  });
}

export function useTransferRequest(id: string) {
  return useQuery({
    queryKey: QK.TRANSFER_REQUEST(id),
    queryFn: () => transferRequestApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateTransferRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (data: CreateTransferRequestPayload) => transferRequestApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-requests'] });
      addToast('success', 'Đăng yêu cầu sang nhượng thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Đăng yêu cầu thất bại')),
  });
}

export function useUpdateTransferRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTransferRequestPayload> }) =>
      transferRequestApi.update(id, data),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['transfer-requests'] });
      qc.invalidateQueries({ queryKey: QK.TRANSFER_REQUEST(variables.id) });
      addToast('success', 'Cập nhật yêu cầu thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật yêu cầu thất bại')),
  });
}

export function useDeleteTransferRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (id: string) => transferRequestApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-requests'] });
      addToast('success', 'Đã xóa yêu cầu');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Xóa yêu cầu thất bại')),
  });
}
